/**
 * Football Schedule Results tab – accordions by week, table per week: Event (teams + logos) | Exact Score | … | DC/O-U with two rows and odds.
 * Mobile (lg): week accordions; per event: exact score only + "View details" button opening modal with full results and odds.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Disclosure, Transition } from '@headlessui/react';
import { useScheduleResultsQuery } from 'features/events/queries';
import { useFootball3dScheduleId } from 'features/events/hooks/useFootball3dScheduleId';
import { getSportColor } from 'shared/utils/sport-colors';
import { vsMarketTypeKey, vsOutcomeTypeKey } from 'lib/virtualSportsI18n';
import { UserSessionContext } from 'features/session/userSessionStore';
import { printOdd } from 'shared/utils/common';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';
import FootballWeekResultsCardGrid from 'features/markets/components/bets-area/FootballWeekResultsCardGrid';
import { FOOTBALL_EXACT_SCORE_MARKET_ID } from 'features/markets/components/bets-area/resultsMobileHelpers';
import { cn } from 'lib/utils';

// Column order: Event | Exact Score | Match Result | Double Chance | Over/Under | 5005 | 5006
const FOOTBALL_MARKET_IDS = [5004, 5001, 5002, 5003, 5005, 5006];
const TWO_ROW_MARKET_IDS = [5002, 5003]; // Double Chance, Over/Under: two outcomes, each with outcome + odd on same line

function formatTime(ts) {
  if (ts == null) return '';
  const d = new Date(Number(ts) * 1000);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function normalizeMarketRaw(raw) {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.map((x) => {
    if (x != null && typeof x === 'object' && ('result' in x || 'odd' in x)) {
      return { result: String(x.result ?? ''), odd: x.odd != null ? Number(x.odd) : null };
    }
    return { result: String(x), odd: null };
  });
}

function FootballOutcomeCell({ raw, t, marketTypeId, oddsFormat = 'decimal', twoRows }) {
  const items = normalizeMarketRaw(raw);
  if (items.length === 0) return <span className="text-muted-foreground">—</span>;
  const label = (otid) => t(vsOutcomeTypeKey(String(otid), false));
  if (twoRows) {
    return (
      <div className="flex flex-col gap-0.5 py-0.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-foreground text-sm font-medium">{label(it.result)}</span>
            {it.odd != null && (
              <span className="text-muted-foreground text-xs tabular-nums">{printOdd(it.odd, oddsFormat)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5 py-0.5">
      {items.map((it, i) => (
        <span key={i} className="text-foreground text-sm inline-flex items-center gap-2">
          <span>{label(it.result)}</span>
          {it.odd != null && (
            <span className="text-muted-foreground text-xs tabular-nums">{printOdd(it.odd, oddsFormat)}</span>
          )}
        </span>
      ))}
    </div>
  );
}

function EventCellWithLogos({ row, showMeta = false }) {
  const homeCode = row.home_team_code ?? '';
  const awayCode = row.away_team_code ?? '';
  const featured = Boolean(row?.featured);
  const eventId = row.event_id != null ? row.event_id : null;
  return (
    <div className="flex items-center gap-2 min-w-0">
      {showMeta && (
        <>
          {featured && (
            <i className="bi bi-camera-video-fill text-sm shrink-0" style={{ color: '#ffd03a' }} aria-hidden title="Live Stream" />
          )}
          {eventId != null && (
            <span className="text-muted-foreground text-xs shrink-0">#{eventId}</span>
          )}
        </>
      )}
      <div className="w-8 h-8 shrink-0 rounded-full bg-muted overflow-hidden flex items-center justify-center">
        <img
          src={getFootball3dTeamLogoUrl(homeCode)}
          alt=""
          className="w-6 h-6 object-contain"
          onError={(e) => { e.target.src = getFootball3dTeamLogoUrl(''); }}
        />
      </div>
      <span className="font-medium text-foreground text-sm truncate min-w-0">
        {homeCode} vs {awayCode}
      </span>
      <div className="w-8 h-8 shrink-0 rounded-full bg-muted overflow-hidden flex items-center justify-center">
        <img
          src={getFootball3dTeamLogoUrl(awayCode)}
          alt=""
          className="w-6 h-6 object-contain"
          onError={(e) => { e.target.src = getFootball3dTeamLogoUrl(''); }}
        />
      </div>
    </div>
  );
}

function getExactScoreLabel(row, t) {
  const raw = row[String(FOOTBALL_EXACT_SCORE_MARKET_ID)] ?? row[FOOTBALL_EXACT_SCORE_MARKET_ID];
  const items = normalizeMarketRaw(raw);
  if (items.length === 0) return '—';
  return items.map((it) => t(vsOutcomeTypeKey(String(it.result), false))).join(', ');
}

export default function FootballScheduleResultsTab() {
  const { t } = useTranslation();
  const [{ user }] = React.useContext(UserSessionContext);
  const scheduleId = useFootball3dScheduleId();
  const { data: results = [], isLoading, isError, refetch, isRefetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useScheduleResultsQuery({ scheduleId });
  const oddsFormat = user?.odds_format || 'decimal';
  const loadMoreSentinelRef = React.useRef(null);
  const fetchNextPageRef = React.useRef(fetchNextPage);
  const wasIntersectingRef = React.useRef(false);
  const didSkipInitialIntersectingRef = React.useRef(false);
  fetchNextPageRef.current = fetchNextPage;

  React.useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    let scrollRoot = null;
    let node = el.parentElement;
    while (node && node !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(node);
      if (['auto', 'scroll', 'overlay'].includes(overflow) || ['auto', 'scroll', 'overlay'].includes(overflowY)) {
        scrollRoot = node;
        break;
      }
      node = node.parentElement;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const isIntersecting = !!entry?.isIntersecting;
        if (isIntersecting) {
          if (!didSkipInitialIntersectingRef.current) {
            didSkipInitialIntersectingRef.current = true;
            wasIntersectingRef.current = true;
            return;
          }
          if (!wasIntersectingRef.current) {
            wasIntersectingRef.current = true;
            fetchNextPageRef.current();
          }
        } else {
          wasIntersectingRef.current = false;
        }
      },
      { root: scrollRoot, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage]);

  const sportColor = getSportColor(50);

  const resultsByWeek = React.useMemo(() => {
    if (!Array.isArray(results) || results.length === 0) return [];
    const byWeek = new Map();
    for (const row of results) {
      const w = row.week_number ?? 0;
      if (!byWeek.has(w)) byWeek.set(w, []);
      byWeek.get(w).push(row);
    }
    return Array.from(byWeek.entries())
      .map(([week, weekResults]) => [
        week,
        [...weekResults].sort((a, b) => {
          const aF = Boolean(a?.featured);
          const bF = Boolean(b?.featured);
          if (aF !== bF) return aF ? -1 : 1;
          return 0;
        }),
      ])
      .sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [results]);

  if (isError) {
    return (
      <div className="p-4 text-sm text-destructive flex flex-col gap-2">
        <span>{t('ERROR_LOADING_DATA') || 'Failed to load data.'}</span>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="self-start px-3 py-1.5 rounded-md text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRefetching ? (t('LOADING') || 'Loading…') : (t('RETRY') || 'Try again')}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        {t('LOADING') || 'Loading…'}
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <div className="football-schedule-results-tab w-full">
        <div
          className="mx-4 mt-4 mb-4 flex items-start gap-3 rounded-lg border border-border bg-muted/30 px-4 py-4 text-left"
          style={{ borderLeftWidth: '4px', borderLeftColor: sportColor }}
        >
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
            aria-hidden
          >
            <i className="bi bi-info-lg text-lg" />
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-foreground">
              {t('NO_RESULTS_YET') || 'No results yet.'}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('NO_RESULTS_YET_DESCRIPTION') || 'There are no completed matches in this schedule yet. Results will appear here once events have finished.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="football-schedule-results-tab w-full">
      {/* Desktop: week accordion + table */}
      <div className="max-lg:hidden divide-y divide-border">
        {resultsByWeek.map(([week, weekResults], weekIndex) => {
          const weekTime = weekResults.length > 0 && weekResults[0].event_timestamp != null
            ? formatTime(weekResults[0].event_timestamp)
            : '';
          const isFirstWeek = weekIndex === 0;
          return (
            <Disclosure key={week} defaultOpen={isFirstWeek && weekResults.length > 0}>
              {({ open }) => (
                <>
                  <Disclosure.Button
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors',
                      open ? 'bg-[var(--sport-light-color)]/20' : 'bg-muted/30 hover:bg-muted/50'
                    )}
                    style={open ? { backgroundColor: 'var(--sport-light-color)' } : undefined}
                  >
                    <span className="font-bold text-foreground">
                      {(t('WEEK_LABEL') || 'Week')} {week}
                      {weekTime ? ` · ${weekTime}` : ''}
                    </span>
                    <span
                      className="inline-flex shrink-0 transition-transform"
                      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      aria-hidden
                    >
                      <i className="bi bi-chevron-down" />
                    </span>
                  </Disclosure.Button>
                  <Transition
                    show={open}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Disclosure.Panel static>
                      <div className="bg-background overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-muted/70 text-left border-b-2 border-border">
                              <th className="px-3 py-2 font-semibold text-foreground whitespace-nowrap min-w-[10rem]">
                                {t('EVENT') || 'Event'}
                              </th>
                              {FOOTBALL_MARKET_IDS.map((mid) => (
                                <th
                                  key={mid}
                                  className="px-3 py-2 font-semibold text-foreground border-l border-border whitespace-nowrap"
                                >
                                  {t(vsMarketTypeKey(mid, false))}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {weekResults
                              .filter((row) => row.event_id != null)
                              .map((row, index) => (
                              <tr
                                key={`${row.event_id}-${index}`}
                                className={cn(
                                  'border-b border-border',
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                                )}
                              >
                                <td className="px-3 py-2 align-middle min-w-[10rem]">
                                  <EventCellWithLogos row={row} showMeta />
                                </td>
                                {FOOTBALL_MARKET_IDS.map((mid) => (
                                  <td key={mid} className="px-3 py-2 border-l border-border align-middle">
                                    <FootballOutcomeCell
                                      raw={row[String(mid)] ?? row[mid]}
                                      t={t}
                                      marketTypeId={mid}
                                      oddsFormat={oddsFormat}
                                      twoRows={TWO_ROW_MARKET_IDS.includes(mid)}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Disclosure.Panel>
                  </Transition>
                </>
              )}
            </Disclosure>
          );
        })}
      </div>

      {/* Mobile: week accordion + compact cards (same as week-finished view) + View details → modal */}
      <div className="lg:hidden divide-y divide-border">
        {resultsByWeek.map(([week, weekResults], weekIndex) => {
          const weekTime = weekResults.length > 0 && weekResults[0].event_timestamp != null
            ? formatTime(weekResults[0].event_timestamp)
            : '';
          const isFirstWeek = weekIndex === 0;
          const sorted = [...weekResults]
            .filter((r) => r.event_id != null)
            .sort((a, b) => {
              const aF = Boolean(a?.featured);
              const bF = Boolean(b?.featured);
              if (aF !== bF) return aF ? -1 : 1;
              return (a?.event_timestamp ?? 0) - (b?.event_timestamp ?? 0);
            })
            .slice(0, 10);
          const mobileRows = sorted.map((r) => ({
            eventId: String(r.event_id),
            result: r,
            homeCode: r.home_team_code ?? '',
            awayCode: r.away_team_code ?? '',
            homeLogoUrl: getFootball3dTeamLogoUrl(r.home_team_code ?? ''),
            awayLogoUrl: getFootball3dTeamLogoUrl(r.away_team_code ?? ''),
            scoreLabel: r.home_score != null && r.away_score != null ? `${Number(r.home_score)} - ${Number(r.away_score)}` : '—',
            featured: Boolean(r.featured),
            eventTimestamp: r.event_timestamp,
          }));

          return (
            <Disclosure key={week} defaultOpen={isFirstWeek && weekResults.length > 0}>
              {({ open }) => (
                <>
                  <Disclosure.Button
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors',
                      open ? 'bg-[var(--sport-light-color)]/20' : 'bg-muted/30 hover:bg-muted/50'
                    )}
                    style={open ? { backgroundColor: 'var(--sport-light-color)' } : undefined}
                  >
                    <span className="font-bold text-foreground">
                      {(t('WEEK_LABEL') || 'Week')} {week}
                      {weekTime ? ` · ${weekTime}` : ''}
                    </span>
                    <span
                      className="inline-flex shrink-0 transition-transform"
                      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      aria-hidden
                    >
                      <i className="bi bi-chevron-down" />
                    </span>
                  </Disclosure.Button>
                  <Transition
                    show={open}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Disclosure.Panel static>
                      <FootballWeekResultsCardGrid
                        rows={mobileRows}
                        showWeekHeader={false}
                        weekNumber={week}
                      />
                    </Disclosure.Panel>
                  </Transition>
                </>
              )}
            </Disclosure>
          );
        })}
      </div>

      {hasNextPage && <div ref={loadMoreSentinelRef} className="h-4 w-full" aria-hidden />}
      {isFetchingNextPage && (
        <div className="flex justify-center py-2 text-sm text-muted-foreground">{t('LOADING') || 'Loading…'}</div>
      )}
    </div>
  );
}
